'use client';

import { useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { ACCOUNT_COPY } from '@eliadmin/core/src/components/dlz/account/account-copy';
import { AccountHeading } from '@eliadmin/core/src/components/dlz/account/account-heading';
import { OrderCard } from '@eliadmin/core/src/components/dlz/account/orders/order-card';
import { OrdersMessage } from '@eliadmin/core/src/components/dlz/account/orders/order-parts';
import {
  orderStage,
  stageFilter,
  type OrderFilter,
} from '@eliadmin/core/src/components/dlz/account/orders/order-stage';
import { ORDERS_COPY } from '@eliadmin/core/src/components/dlz/account/orders/orders-copy';
import {
  orderNumber,
  searchText,
  useCustomerOrders,
} from '@eliadmin/core/src/components/dlz/account/orders/orders-data';
import { SearchField } from '@eliadmin/core/src/components/dlz/home/stores/search-field';
import { ChoiceChip } from '@eliadmin/core/src/components/dlz/ui/chip';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/** The design lists four orders, „Zobraziť staršie objednávky“ adds the next ten. */
const FIRST = 4;
const MORE = 10;

type ListedOrder = { order: CustomerOrder; filter: Exclude<OrderFilter, 'all'>; text: string };

/**
 * Figma „Moje objednávky“ (60323:19774, phones 60459:90475): heading, the toolbar (filter chips
 * with counts, search by order number or product name), the order cards and „Zobraziť staršie
 * objednávky (N)“. The whole list is loaded once; filtering and search run on the client. The chips
 * scroll sideways on phones (to the screen edge); the search sits beside them only in the 877 px
 * column of the 1728 frame, narrower under them.
 */
export function OrdersList() {
  const { t } = useTranslations();
  const { data: orders, isPending, isError, refetch, isRefetching } = useCustomerOrders();
  const [filter, setFilter] = useState<OrderFilter>('all');
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(FIRST);
  const list = useRef<HTMLOListElement>(null);

  const listed = useMemo<ListedOrder[]>(
    () =>
      (orders ?? []).map(order => ({
        order,
        filter: stageFilter(orderStage(order)),
        text: searchText([orderNumber(order), ...order.items.map(item => item.name)].join(' ')),
      })),
    [orders],
  );

  const filters: { value: OrderFilter; label: string }[] = [
    { value: 'all', label: t('text1947') },
    { value: 'active', label: t('text2085') },
    { value: 'delivered', label: ORDERS_COPY.filters.delivered },
    { value: 'cancelled', label: ORDERS_COPY.filters.cancelled },
  ];
  const count = (value: OrderFilter) =>
    value === 'all' ? listed.length : listed.filter(item => item.filter === value).length;

  const needle = searchText(query);
  const matches = listed.filter(
    item => (filter === 'all' || item.filter === filter) && item.text.includes(needle),
  );
  const older = matches.length - visible;

  const choose = (value: OrderFilter) => {
    setFilter(value);
    setVisible(FIRST);
  };
  const search = (value: string) => {
    setQuery(value);
    setVisible(FIRST);
  };
  const reset = () => {
    choose('all');
    setQuery('');
  };
  // Keyboard users continue at the first card that appeared (the button may be gone).
  const showOlder = () => {
    const first = visible;
    flushSync(() => setVisible(first + MORE));
    list.current?.children[first]?.querySelector<HTMLElement>('a')?.focus();
  };

  const heading = (
    <AccountHeading
      title={ACCOUNT_COPY.pages.orders.label}
      description={ACCOUNT_COPY.pages.orders.description}
    />
  );

  if (isPending) {
    return (
      <div className="flex flex-col gap-6">
        {heading}
        <GenericLoader className="h-80" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        {heading}
        <OrdersMessage
          icon="warning"
          title={ORDERS_COPY.loadFailed}
          alert
          action={
            <Button variant="secondary" onClick={() => refetch()} isLoading={isRefetching}>
              {t('text595')}
            </Button>
          }
        />
      </div>
    );
  }

  if (!listed.length) {
    return (
      <div className="flex flex-col gap-6">
        {heading}
        <OrdersMessage
          icon="package"
          title={ORDERS_COPY.empty.title}
          text={ORDERS_COPY.empty.text}
          action={
            <Button asChild className="h-12 w-full md:h-[52px] md:w-auto">
              <Link href={ROUTES.HOME() ?? '/'} prefetch={false}>
                {ORDERS_COPY.empty.start}
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {heading}

      <div className="flex flex-col gap-4 @[54rem]:flex-row @[54rem]:items-center @[54rem]:justify-between">
        <div
          role="group"
          aria-label={ORDERS_COPY.filters.label}
          className="hide-scroll-bar -m-1 flex gap-2 overflow-x-auto p-1 max-md:-mr-4 max-md:pr-4"
        >
          {filters.map(option => (
            <ChoiceChip
              key={option.value}
              selected={filter === option.value}
              onSelect={() => choose(option.value)}
              className="dlz-body-s-medium h-10 px-3 @[30rem]:h-[42px] @[30rem]:px-4"
            >
              {ORDERS_COPY.count(option.label, count(option.value))}
            </ChoiceChip>
          ))}
        </div>
        <div className="@[54rem]:w-[330px]">
          <SearchField label={ORDERS_COPY.search} value={query} onChange={search} />
        </div>
      </div>

      <p aria-live="polite" className="sr-only">
        {filter !== 'all' || needle ? ORDERS_COPY.found(matches.length) : ''}
      </p>

      {matches.length ? (
        <ol ref={list} className="flex flex-col gap-4">
          {matches.slice(0, visible).map(({ order }) => (
            <li key={order.id}>
              <OrderCard order={order} />
            </li>
          ))}
        </ol>
      ) : (
        <OrdersMessage
          icon="search"
          title={ORDERS_COPY.noResults.title}
          text={ORDERS_COPY.noResults.text}
          action={
            <Button variant="secondary" onClick={reset}>
              {ORDERS_COPY.noResults.reset}
            </Button>
          }
        />
      )}

      {older > 0 ? (
        <Button
          variant="secondary"
          onClick={showOlder}
          className="h-12 w-full md:h-[52px] md:w-auto md:self-center"
        >
          {ORDERS_COPY.more(older)}
        </Button>
      ) : null}
    </div>
  );
}
